"use client";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@mantine/core";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { updateBankAccountMutation } from "@/app/(logedIn)/mypage/bankAccount/_graphql";

import { Button, Center, Group, H2, Stack } from "@/components/ui/elements";
import { AlertField, InputField, SelectField } from "@/components/ui/parts";

import type { BankAccount } from "@/gql/__generated__/graphql";
import { infoMessage } from "@/i18n";
import {
  accountHolderValidation,
  accountNumberValidation,
  accountTypeValidation,
  bankcodeValidation,
  bankNameValidation,
  branchcodeValidation,
  branchNameValidation,
} from "@/types/validation";
import { showNotification } from "@/util/userNotifications";

import { BankNameInput } from "./BankNameInput";
import { BranchNameInput } from "./BranchNameInput";

type Props = {
  defaultValue?: {
    accountHolderName: string;
    accountNumber: string;
    accountType: BankAccount;
    bankCode: string;
    bankName: string;
    branchCode: string;
    branchName: string;
    id: string;
  };
  destinationPath: string;
  isMyPage?: boolean;
  isStepRegistration?: boolean;
  onCompleted?: () => void;
};

const schema = z.object({
  bankCode: bankcodeValidation(),
  bankName: bankNameValidation(),
  branchCode: branchcodeValidation(),
  branchName: branchNameValidation(),
  accountType: accountTypeValidation(),
  accountNumber: accountNumberValidation(),
  accountHolderLastName: accountHolderValidation(),
  accountHolderFirstName: accountHolderValidation(),
});

type Schema = z.infer<typeof schema>;

export function BankForm({
  destinationPath,
  defaultValue,
  isStepRegistration = false,
  onCompleted,
}: Props) {
  const router = useRouter();
  const [bankLoading, setBankLoading] = useState(false);
  const [branchReloadToken, setBranchReloadToken] = useState(0);

  // 検索状態を親コンポーネントで管理（金融機関名のみ）
  const [bankSearchValue, setBankSearchValue] = useState(
    defaultValue?.bankName || "",
  );

  // 編集時の初期化
  useEffect(() => {
    if (defaultValue?.bankName && bankSearchValue !== defaultValue.bankName) {
      setBankSearchValue(defaultValue.bankName);
    }
  }, [defaultValue?.bankName]);

  const { control, handleSubmit, watch, getValues, setValue } = useForm<Schema>(
    {
      mode: "onBlur",
      resolver: zodResolver(schema),
      defaultValues: defaultValue
        ? {
            bankCode: defaultValue.bankCode,
            bankName: defaultValue.bankName,
            branchCode: defaultValue.branchCode,
            branchName: defaultValue.branchName,
            accountType: defaultValue.accountType,
            accountNumber: defaultValue.accountNumber,
            accountHolderLastName:
              defaultValue.accountHolderName.split("　")[0], // 全角スペースで分ける
            accountHolderFirstName:
              defaultValue.accountHolderName.split("　")[1], // 全角スペースで分ける
          }
        : {
            bankCode: undefined,
            bankName: undefined,
            branchCode: undefined,
            branchName: undefined,
            accountType: "ordinary",
            accountNumber: undefined,
            accountHolderLastName: undefined,
            accountHolderFirstName: undefined,
          },
    },
  );

  const [mutation, { loading }] = useMutation(updateBankAccountMutation, {
    refetchQueries: ["BankAccount", "Viewer", "RegistrationSteps"],
  });
  const onSubmit = async (data: Schema) => {
    await mutation({
      variables: {
        input: {
          accountHolderName: `${data.accountHolderLastName}　${data.accountHolderFirstName}`,
          accountNumber: data.accountNumber,
          accountType: data.accountType,
          bankCode: data.bankCode,
          bankName: data.bankName,
          branchCode: data.branchCode,
          branchName: data.branchName,
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.updateBankAccount;
        if (!res) return;
        showNotification({ type: "info", message: infoMessage("addBank") });
        if (onCompleted) {
          onCompleted();
          return;
        }
        router.push(destinationPath);
      },
    });
  };

  const getBankCode = (code: string) => {
    const currentCode = getValues("bankCode");
    setValue("bankCode", code);

    if (code === "") {
      setBankSearchValue("");
      return;
    }

    if (code !== currentCode) {
      setValue("branchCode", "");
      setValue("branchName", "");
    }

    // 同じ金融機関の再選択時も支店一覧を再取得する
    setBranchReloadToken((token) => token + 1);

    setBankLoading(true);
    setTimeout(() => {
      setBankLoading(false);
    }, 100);
  };

  const getBankName = (name: string) => {
    setValue("bankName", name);
    setBankSearchValue(name);
  };

  const getBranchCode = (code: string) => {
    setValue("branchCode", code);
  };

  const getBranchName = (name: string) => {
    setValue("branchName", name);
  };

  const defaultbank = getValues("bankCode");
  const defaultbankName = getValues("bankName");
  const defaultbranch = getValues("branchCode");
  const defaultbranchName = getValues("branchName");

  const infoMessages = `・振込先口座の確認は行っておりません。口座情報はお間違いのないようご登録ください。
・振込手数料は当社が負担します。口座情報の不備等により振込ができなかった場合は、口座情報をご確認のうえ、もう一度振込申請してください。`;
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <H2>報酬を受け取る口座を登録してください。</H2>
        <Group align="flex-end">
          <BankNameInput
            defaultbank={defaultbank}
            defaultbankName={defaultbankName}
            searchValue={bankSearchValue}
            onSearchChange={setBankSearchValue}
            getBankCode={getBankCode}
            getBankName={getBankName}
          />
          <InputField
            maw={150}
            label="金融機関コード"
            description="※ こちらの欄は自動入力のため入力は不要です。"
            name="bankCode"
            control={control}
            disabled
          />
        </Group>
        <Group align="flex-end">
          <BranchNameInput
            defaultbranch={defaultbranch}
            defaultbranchName={defaultbranchName}
            bankcode={watch("bankCode")}
            branchReloadToken={branchReloadToken}
            getBranchCode={getBranchCode}
            getBranchName={getBranchName}
            disabled={bankLoading || !watch("bankCode")}
          />
          <InputField
            maw={150}
            label="支店コード"
            description="※ こちらの欄は自動入力のため入力は不要です。"
            name="branchCode"
            control={control}
            disabled
          />
        </Group>

        <SelectField
          withAsterisk
          maw={200}
          label="口座種別"
          name="accountType"
          control={control}
          data={[
            { value: "ordinary", label: "普通" },
            { value: "checking", label: "当座" },
          ]}
        />
        <InputField
          withAsterisk
          maw={200}
          label="口座番号7桁"
          name="accountNumber"
          control={control}
        />
        <div>
          <Input.Label required>口座名義人※カタカナのみ</Input.Label>
          <Group align="flex-end">
            <InputField
              withAsterisk
              maw={200}
              label="セイ"
              name="accountHolderLastName"
              control={control}
            />
            <InputField
              withAsterisk
              maw={200}
              label="メイ"
              name="accountHolderFirstName"
              control={control}
            />
          </Group>
        </div>

        <Center>
          <Button color="teal" type="submit" loading={loading}>
            {isStepRegistration ? "登録して次へ" : "口座を登録する"}
          </Button>
        </Center>

        <Center>
          <AlertField
            type="notice"
            maw={500}
            title="info"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {infoMessages}
          </AlertField>
        </Center>
      </Stack>
    </form>
  );
}
